import axios from 'axios';
import { env } from '../config/env';
import type { Skill } from '../modules/TechnicalInterview';

interface SkillsResponse {
  skills: Array<{
    name: string;
    description: string;
  }>;
}

export type Seniority = 'specialist' | 'senior' | 'pleno' | 'junior' | 'trainee';

export const generateSkillsFromText = async (text: string): Promise<Skill[]> => {
  try {
    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `Você é um especialista em RH técnico, especializado em extrair skills técnicas de descrições de vagas.
            
            Analise o texto fornecido e extraia as skills técnicas relevantes.
            Para cada skill, forneça:
            1. Um nome conciso
            2. Uma descrição detalhada do que deve ser avaliado
            
            Retorne apenas um JSON válido com a seguinte estrutura:
            {
              "skills": [
                {
                  "name": "Nome da Skill",
                  "description": "Descrição detalhada do que avaliar"
                }
              ]
            }`
          },
          {
            role: 'user',
            content: `Extraia as skills técnicas do seguinte texto:\n\n${text}`
          }
        ],
        temperature: 0.3,
        max_tokens: 2000,
        response_format: { type: "json_object" }
      },
      {
        headers: {
          'Authorization': `Bearer ${env.chatGptApiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const content = response.data?.choices?.[0]?.message?.content;
    if (!content) {
      throw new Error('Conteúdo da resposta não encontrado');
    }

    const parsedContent = JSON.parse(content) as SkillsResponse;
    
    return parsedContent.skills.map(skill => ({
      id: crypto.randomUUID(),
      name: skill.name,
      description: skill.description,
      evaluation: null
    }));
  } catch (error) {
    console.error('Erro ao gerar skills:', error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Chave de API inválida ou expirada');
      }
      if (error.response?.status === 429) {
        throw new Error('Limite de requisições excedido');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Erro na API de IA');
      }
    }
    throw new Error('Não foi possível gerar as skills');
  }
};

export const generateSkillsFromPosition = async (position: string, seniority: Seniority): Promise<Skill[]> => {
  try {
    const seniorityLabels = {
      specialist: 'Especialista',
      senior: 'Sênior',
      pleno: 'Pleno',
      junior: 'Júnior',
      trainee: 'Trainee',
    };

    const prompt = `
      Gere uma lista de skills técnicas para avaliar um candidato à vaga de ${position} (${seniorityLabels[seniority]}).
      
      Considere:
      - O nível de senioridade na definição das skills
      - Conhecimentos técnicos específicos da posição
      - Boas práticas e padrões relevantes
      - Experiência com ferramentas e tecnologias comuns
      - Capacidade de resolução de problemas
      - Arquitetura e design de soluções
      
      Para cada skill, forneça:
      - Nome claro e objetivo
      - Descrição detalhada do que deve ser avaliado
    `;

    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `Você é um especialista em RH técnico, especializado em extrair skills técnicas de descrições de vagas.
            
            Analise o texto fornecido e extraia as skills técnicas relevantes.
            Para cada skill, forneça:
            1. Um nome conciso
            2. Uma descrição detalhada do que deve ser avaliado
            
            Retorne apenas um JSON válido com a seguinte estrutura:
            {
              "skills": [
                {
                  "name": "Nome da Skill",
                  "description": "Descrição detalhada do que avaliar"
                }
              ]
            }`
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 2000,
        response_format: { type: "json_object" }
      },
      {
        headers: {
          'Authorization': `Bearer ${env.chatGptApiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const content = response.data?.choices?.[0]?.message?.content;
    if (!content) {
      throw new Error('Conteúdo da resposta não encontrado');
    }

    const parsedContent = JSON.parse(content) as SkillsResponse;
    
    return parsedContent.skills.map(skill => ({
      id: crypto.randomUUID(),
      name: skill.name,
      description: skill.description,
      evaluation: null
    }));
  } catch (error) {
    console.error('Erro ao gerar skills:', error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Chave de API inválida ou expirada');
      }
      if (error.response?.status === 429) {
        throw new Error('Limite de requisições excedido');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error.message || 'Erro na API de IA');
      }
    }
    throw new Error('Não foi possível gerar as skills');
  }
};